import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { SharedActions } from '@states/shared/shared.action-types';
import { WorkflowActions } from '@states/workflow/workflow.action.types';
import { catchError, share, switchMap } from 'rxjs';
import { UtilsService } from 'src/app/edge/utils.service';
import { ContextualLoggerService } from 'src/app/services/contextual-logger.service';
import { LoggerService } from 'src/app/services/logger.service';
import { WorkflowService } from 'src/app/services/workflow.service';

@Injectable()
export class WorkflowEffect {
  private baseLogger = inject(LoggerService);
  private context = 'WorkflowEffect';
  private logger = new ContextualLoggerService(this.baseLogger, this.context);

  constructor(
    private actions$: Actions,
    private utilsService: UtilsService,
    private translateService: TranslateService,
    private workflowService: WorkflowService,
  ) { }

  public getWorkflows$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflows),
      switchMap(({ orgId }) => {
        return [
          WorkflowActions.resetState(),
          WorkflowActions.setIsLoading({ isLoading: true }),
          WorkflowActions.getWorkflowsServerCall({ orgId }),
        ];
      }),
    ),
  );

  public getWorkflowsServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowsServerCall),
      switchMap(({ orgId }) => {
        return this.workflowService.getWorkflows(orgId).pipe(
          switchMap((workflows) => {
            return [
              WorkflowActions.getWorkflowsSuccess({
                workflows: workflows,
              }),
              WorkflowActions.setIsLoading({ isLoading: false }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowActions.setIsLoading({ isLoading: false }),
              WorkflowActions.getWorkflowshFail,
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public initGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.initGraph),
      switchMap(({ graph }) => {
        return [
          WorkflowActions.setIsLoading({ isLoading: true }),
          WorkflowActions.initGraphServerCall({ graph: graph }),
        ];
      }),
    ),
  );

  public initGraphServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.initGraphServerCall),
      switchMap(({ graph }) => {
        return this.workflowService.initGraph(graph).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.initGraphSuccess({
                graph: graph,
              }),
              WorkflowActions.setIsLoading({ isLoading: false }),
              SharedActions.showMessage({ success: this.translateService.instant('FLOW_CHART.TOASTS.GRAPH_CREATED_SUCCESS') }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowActions.setIsLoading({ isLoading: false }),
              WorkflowActions.initGraphFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public getGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getGraph),
      switchMap(({ id, orgId }) => {
        return [
          WorkflowActions.setIsLoading({ isLoading: true }),
          WorkflowActions.getGraphServerCall({ id: id, orgId: orgId }),
        ];
      }),
    ),
  );

  public getGraphServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getGraphServerCall),
      switchMap(({ id, orgId }) => {
        return this.workflowService.getGraph(id, orgId).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.getGraphSuccess({
                graph: graph,
              }),
              WorkflowActions.setIsLoading({ isLoading: false }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowActions.setIsLoading({ isLoading: false }),
              WorkflowActions.getGraphFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public removeWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.removeWorkflow),
      switchMap(({ id, orgId }) => {
        return [
          WorkflowActions.setIsLoading({ isLoading: true }),
          WorkflowActions.removeWorkflowServerCall({ id, orgId }),
        ];
      }),
    ),
  );

  public removeWorkflowServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.removeWorkflowServerCall),
      switchMap(({ id, orgId }) => {
        return this.workflowService.removeWorkflow(id, orgId).pipe(
          switchMap(() => {
            return [
              WorkflowActions.removeWorkflowSuccess({ id }),
              WorkflowActions.setIsLoading({ isLoading: false }),
              SharedActions.showMessage({ success: this.translateService.instant('FLOW_CHART.TOASTS.GRAPH_DELETED_SUCCESS') }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowActions.setIsLoading({ isLoading: false }),
              WorkflowActions.removeWorkflowFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public updateGraph$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.updateGraph),
      switchMap(({ graphMetadata, id, orgId }) => {
        return this.workflowService.updateGraph(graphMetadata, id, orgId).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.updateGraphSuccess({ graph: graph }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowActions.updateGraphFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public initNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.initNode),
      switchMap(({ orgId, graphId, node }) => {
        return [
          WorkflowActions.initNodeServerCall({ orgId, graphId, node }),
        ];
      }),
    ),
  );

  public initNodeServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.initNodeServerCall),
      switchMap(({ orgId, graphId, node }) => {
        return this.workflowService.initNode(orgId, graphId, node).pipe(
          switchMap((node) => {
            return [
              WorkflowActions.initNodeSuccess({
                node: node,
              }),
              WorkflowActions.getGraphServerCall({ id: graphId, orgId: orgId }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowActions.initNodeFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public editNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.editNode),
      switchMap(({ orgId, graphId, id, updateNodeOptions }) => {
        return [
          WorkflowActions.editNodeServerCall({
            orgId,
            graphId,
            id,
            updateNodeOptions,
          }),
        ];
      }),
    ),
  );

  public editNodeServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.editNodeServerCall),
      switchMap(({ orgId, graphId, id, updateNodeOptions }) => {
        return this.workflowService
          .editNode(orgId, graphId, id, updateNodeOptions)
          .pipe(
            switchMap((node) => {
              return [
                WorkflowActions.editNodeSuccess({
                  node: node,
                }),
              ];
            }),
            catchError((response) => {
              return [
                this.catchError(response),
                WorkflowActions.editNodeFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public getNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getNode),
      switchMap(({ orgId, graphId, id }) => {
        return [
          WorkflowActions.setIsNodeLoading({ isLoading: true }),
          WorkflowActions.getNodeServerCall({
            orgId,
            graphId,
            id,
          }),
        ];
      }),
    ),
  );

  public getNodeServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getNodeServerCall),
      switchMap(({ orgId, graphId, id }) => {
        return this.workflowService
          .getNode(orgId, graphId, id)
          .pipe(
            switchMap((node) => {
              return [
                WorkflowActions.getNodeSuccess({
                  node: node,
                }),
                WorkflowActions.setIsNodeLoading({ isLoading: false }),
              ];
            }),
            catchError((response) => {
              const errorMessage = this.utilsService.errMessage(response);
              this.logger.error(errorMessage);

              return [
                WorkflowActions.setIsNodeLoading({ isLoading: false }),
                WorkflowActions.getNodeFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public deleteNode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.deleteNode),
      switchMap(({ orgId, graphId, id }) => {
        return [
          WorkflowActions.deleteNodeServerCall({
            orgId,
            graphId,
            id,
          }),
        ];
      }),
    ),
  );

  public deleteNodeServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.deleteNodeServerCall),
      switchMap(({ orgId, graphId, id }) => {
        return this.workflowService.deleteNode(orgId, graphId, id).pipe(
          switchMap(() => {
            return [
              WorkflowActions.deleteNodeSuccess(),
              WorkflowActions.getGraphServerCall({ id: graphId, orgId: orgId }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response), WorkflowActions.deleteNodeFail()];
          }),
        );
      }),
      share(),
    ),
  );

  public updateConnections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.updateConnections),
      switchMap(({ orgId, graphId, updateConnections }) => {
        return [
          WorkflowActions.updateConnectionsServerCall({
            orgId,
            graphId,
            updateConnections,
          }),
        ];
      }),
    ),
  );

  public updateConnectionsServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.updateConnectionsServerCall),
      switchMap(({ orgId, graphId, updateConnections }) => {
        return this.workflowService
          .updateConnections(orgId, graphId, updateConnections)
          .pipe(
            switchMap((graph) => {
              return [
                WorkflowActions.updateConnectionsSuccess({
                  graph: graph,
                  orgId,
                  graphId,
                }),
              ];
            }),
            catchError((response) => {
              return [
                this.catchError(response),
                WorkflowActions.updateConnectionsFail(),
              ];
            }),
          );
      }),
      share(),
    ),
  );

  public updateConnectionsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.updateConnectionsSuccess),
      switchMap(({ orgId, graphId }) => {
        return [
          WorkflowActions.publishGraphWorkflow({
            orgId,
            graphId,
          }),
        ];
      }),
    ),
  );

  public runGraphWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.runGraphWorkflow),
      switchMap(({ orgId, graphId, runGraph }) => {
        return this.workflowService.runGraph(orgId, graphId, runGraph).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('FLOW_CHART.TOASTS.GRAPH_RUN_SUCCESS') }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response),];
          }),
        );
      }),
      share(),
    ),
  );

  public publishGraphWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.publishGraphWorkflow),
      switchMap(({ orgId, graphId }) => {
        return this.workflowService.publishGraph(orgId, graphId).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.publishGraphWorkflowSuccess({
                graph: graph,
              }),
              SharedActions.showMessage({ success: this.translateService.instant('FLOW_CHART.TOASTS.GRAPH_PUBLISH_SUCCESS') }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response),];
          }),
        );
      }),
      share(),
    ),
  );

  public enableWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.enableWorkflow),
      switchMap(({ orgId, graphId }) => {
        return this.workflowService.enableGraph(orgId, graphId).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.changeGraphStatusSuccess({
                graph: graph,
              }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response),];
          }),
        );
      }),
      share(),
    ),
  );

  public disableWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.disableWorkflow),
      switchMap(({ orgId, graphId }) => {
        return this.workflowService.disableGraph(orgId, graphId).pipe(
          switchMap((graph) => {
            return [
              WorkflowActions.changeGraphStatusSuccess({
                graph: graph,
              }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response),];
          }),
        );
      }),
      share(),
    ),
  );

  public getWorkflowRunList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowRunList),
      switchMap(({ orgId, graphId }) => {
        return [
          WorkflowActions.getWorkflowRunListServerCall({ orgId, graphId }),
        ];
      }),
    ),
  );

  public getWorkflowRunListServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowRunListServerCall),
      switchMap(({ orgId, graphId }) => {
        return this.workflowService.getGraphRunList(orgId, graphId).pipe(
          switchMap((runList) => {
            return [
              WorkflowActions.getWorkflowRunListSuccess({
                runList: runList,
              }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowActions.getWorkflowRunListFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public cancelRunWorkflow$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.cancelRunWorkflow),
      switchMap(({ orgId, graphId, runId }) => {
        return this.workflowService.cancelGraphRun(orgId, graphId, runId).pipe(
          switchMap(() => {
            return [
              WorkflowActions.getWorkflowRunList({
                orgId: orgId,
                graphId: graphId,
              }),
            ];
          }),
          catchError((response) => {
            return [this.catchError(response),];
          }),
        );
      }),
      share(),
    ),
  );

  public getWorkflowRunSteps$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowRunSteps),
      switchMap(({ orgId, graphId, runId, view }) => {
        return [
          WorkflowActions.getWorkflowRunStepsServerCall({ orgId, graphId, runId, view }),
        ];
      }),
    ),
  );

  public getWorkflowRunStepsServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowRunStepsServerCall),
      switchMap(({ orgId, graphId, runId, view }) => {
        return this.workflowService.getGraphRunSteps(orgId, graphId, runId, view).pipe(
          switchMap((steps) => {
            return [
              WorkflowActions.getWorkflowRunStepsSuccess({
                steps: steps,
              }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowActions.getWorkflowRunStepsFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public getWorkflowRunStepInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowActions.getWorkflowRunStepInfo),
      switchMap(({ orgId, graphId, runId, stepId }) => {
        return this.workflowService.getGraphRunStepInfo(orgId, graphId, runId, stepId).pipe(
          switchMap((step) => {
            return [
              WorkflowActions.getWorkflowRunStepInfoSuccess({
                step: step,
              }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowActions.getWorkflowRunStepInfoFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  private catchError(response) {
    const errorMessage = this.utilsService.errMessage(response);
    return SharedActions.showMessage({ error: errorMessage });
  }
}
